import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import { useDealLabelsStore } from 'dashboard/stores/dealLabels';

export const LABEL_ENTITIES = ['conversation', 'contact', 'company', 'deal'];

// Normalizes the 4 independent label pools (Vuex `labels` for Conversation,
// Pinia stores for Contact/Company/Deal) behind one interface, so the
// Settings > Labels page can render a single table/form regardless of the
// active tab.
export function useLabelSourceByEntity(entityKey) {
  const store = useStore();
  const getters = useStoreGetters();
  const contactLabelsStore = useContactLabelsStore();
  const companyLabelsStore = useCompanyLabelsStore();
  const dealLabelsStore = useDealLabelsStore();

  const records = computed(() => {
    switch (entityKey.value) {
      case 'contact':
        return contactLabelsStore.getLabels;
      case 'company':
        return companyLabelsStore.getLabels;
      case 'deal':
        return dealLabelsStore.getLabels;
      default:
        return getters['labels/getLabels'].value;
    }
  });

  const uiFlags = computed(() => {
    switch (entityKey.value) {
      case 'contact':
        return {
          isFetching: contactLabelsStore.isFetching,
          isCreating: contactLabelsStore.isCreating,
          isUpdating: contactLabelsStore.isUpdating,
          isDeleting: contactLabelsStore.isDeleting,
        };
      case 'company':
        return {
          isFetching: companyLabelsStore.isFetching,
          isCreating: companyLabelsStore.isCreating,
          isUpdating: companyLabelsStore.isUpdating,
          isDeleting: companyLabelsStore.isDeleting,
        };
      case 'deal':
        return {
          isFetching: dealLabelsStore.isFetching,
          isCreating: dealLabelsStore.isCreating,
          isUpdating: dealLabelsStore.isUpdating,
          isDeleting: dealLabelsStore.isDeleting,
        };
      default:
        return getters['labels/getUIFlags'].value;
    }
  });

  const fetchAll = () => {
    switch (entityKey.value) {
      case 'contact':
        return contactLabelsStore.fetchAll();
      case 'company':
        return companyLabelsStore.fetchAll();
      case 'deal':
        return dealLabelsStore.fetchAll();
      default:
        return store.dispatch('labels/get');
    }
  };

  const create = attrs => {
    switch (entityKey.value) {
      case 'contact':
        return contactLabelsStore.create(attrs);
      case 'company':
        return companyLabelsStore.create(attrs);
      case 'deal':
        return dealLabelsStore.create(attrs);
      default:
        return store.dispatch('labels/create', attrs);
    }
  };

  const update = (id, attrs) => {
    switch (entityKey.value) {
      case 'contact':
        return contactLabelsStore.update(id, attrs);
      case 'company':
        return companyLabelsStore.update(id, attrs);
      case 'deal':
        return dealLabelsStore.update(id, attrs);
      default:
        return store.dispatch('labels/update', { id, ...attrs });
    }
  };

  const destroy = id => {
    switch (entityKey.value) {
      case 'contact':
        return contactLabelsStore.delete(id);
      case 'company':
        return companyLabelsStore.delete(id);
      case 'deal':
        return dealLabelsStore.delete(id);
      default:
        return store.dispatch('labels/delete', id);
    }
  };

  return {
    records,
    uiFlags,
    fetchAll,
    create,
    update,
    destroy,
  };
}
